import {LngLat, LngLatBounds, Map, Marker, Popup} from 'maplibre-gl';
import turfCircle from '@turf/circle';

/** @type {Map} */
let map;
/** @type {Marker[]} */
let markers = [];
/** @type {Popup[]} */
let popups = [];

/** @param {{}} point */
const generateMarker = (point) => {
    const markerElement = document.createElement('img');

    const marker = new Marker({
        element: markerElement,
        anchor: 'bottom'
    })
        .setLngLat(new LngLat(point.lng, point.lat))
        .addTo(map);

    marker.id = point.id;
    markerElement.src = '/images/map-pin.svg';
    markerElement.addEventListener('mouseover', () => {
        Livewire.emit('alentoor_map:marker:highlighted', point.id);
        map.panTo(marker.getLngLat(), {
            duration: 1000
        });
    });
    markers.push(marker);

    return marker;
};

/** @param {{}} point */
const generateCircle = (point) => {
    map.addLayer({
        id: `bras-immobilier-circle-${point.id}`,
        metadata: {
            lngLat: new LngLat(point.lng, point.lat)
        },
        type: 'fill',
        source: {
            type: 'geojson',
            data: turfCircle([point.lng, point.lat], app.ads_purchase_radius, {
                steps: 64,
                units: 'meters'
            })
        },
        layout: {},
        paint: {
            'fill-color': '#be1622',
            'fill-opacity': .35
        }
    });
};

/**
 *
 * @param {{}} point
 * @param {Marker} marker
 */
const generatePopup = (point, marker = null) => {
    if (! point.tooltip) {
        return;
    }

    const popup = new Popup({
        maxWidth: '220px',
        closeButton: false,
        offset: 20,
        className: 'maplibregl-popup-custom',
        anchor: 'bottom'
    });

    popup.id = point.id;
    popup.setHTML(point.tooltip);

    if (marker) {
        marker.setPopup(popup);
        marker.getElement().addEventListener('mouseenter', () => {
            popups.forEach((popup) => popup.remove());
            popup.addTo(map);
        });
    } else {
        popup.bras_immobilier_extras = {
            handler: () => {
                popups.forEach((popup) => popup.remove());
                popup.setLngLat(new LngLat(point.lng, point.lat));
                popup.addTo(map);
                Livewire.emit('alentoor_map:marker:highlighted', point.id);
                map.panTo(new LngLat(point.lng, point.lat), {
                    duration: 1000
                });
            },
            layer_id: `bras-immobilier-circle-${point.id}`
        };

        map.on('mouseenter', `bras-immobilier-circle-${point.id}`, popup.bras_immobilier_extras.handler);
    }

    popups.push(popup);
};

const closeAllInfoWindowsOnMapClick = () => {
    map.on('click', () => {
        popups.forEach((popup) => popup.remove());
        Livewire.emit('alentoor_map:markers:highlight:cancel');
    });
};

const highlightMarkerFromEvent = (event) => {
    //Livewire.emit('alentoor_map:marker:highlighted', event.detail.id);

    const marker = markers.find((marker) => marker.id === parseInt(event.detail.id));
    const layer = map.getLayer(`bras-immobilier-circle-${event.detail.id}`);
    const popup = popups.find((popup) => popup.id === parseInt(event.detail.id));

    popups.forEach((popup) => popup.remove());

    let popupLngLat;

    if (marker) {
        if (marker.radius) {
            popupLngLat = marker.getCenter();
        } else {
            popupLngLat = marker.getLngLat();
        }
    } else {
        if (layer) {
            popupLngLat = map.getLayer(`bras-immobilier-circle-${event.detail.id}`).metadata.lngLat;
        }
    }

    if (popupLngLat) {
        popup.setLngLat(popupLngLat);
        map.panTo(popupLngLat, {
            duration: 1000
        });
    }

    if (popup) {
        setTimeout(() => {
            popup.addTo(map);
        }, 1000);
    }
};

const resetMarkersAndPopups = () => {
    markers.forEach((marker) => marker.remove());
    popups.forEach((popup) => {
        if (popup.bras_immobilier_extras) {
            map.off('mouseenter', popup.bras_immobilier_extras.layer_id, popup.bras_immobilier_extras.handler);
        }

        popup.remove();
    });
    map.getStyle().layers.forEach((layer) => {
        if (layer.id.startsWith('bras-immobilier-circle-')) {
            map.removeLayer(layer.id);
            map.removeSource(layer.source);
        }
    });

    markers = [];
    popups = [];
};

const defaultCenterAndZoomMap = () => {
    map.setCenter(new LngLat(-1.6492509710537808, 47.20213056470701));
    map.setZoom(8);
};

const generateMarkersAndPopups = (points) => {
    const bounds = new LngLatBounds();

    if (points.length) {
        points.forEach((point) => {
            const marker = generateMarker(point);

            generatePopup(point, marker);
            bounds.extend(marker.getLngLat());
        });
        map.fitBounds(bounds, {
            maxZoom: app.map_default_zoom,
            padding: 5
        });
    } else {
        defaultCenterAndZoomMap();
    }
};

const generateCirclesAndPopups = (points) => {
    const bounds = new LngLatBounds();

    if (points.length) {
        points.forEach((point) => {
            generateCircle(point);
            generatePopup(point);
            bounds.extend(new LngLat(point.lng, point.lat));
        });
        map.fitBounds(bounds, {
            maxZoom: app.map_default_zoom,
            padding: 5
        });
    } else {
        defaultCenterAndZoomMap();
    }
};

export default class AlentoorMap {
    static init() {
        window.addEventListener('alentoor_map:init', (event) => {
            map = new Map({
                container: 'alentoor-map',
                style: 'https://map.alentoor.fr/assets/styles/default/style.json?key=db488d4cbaced53094509e59be11f61b',
                zoom: 10,
                center: new LngLat(-1.6492509710537808, 47.20213056470701),
                cooperativeGestures: true
            });
            map.on('load', () => {
                map.resize();

                if (event.detail.useMarker) {
                    generateMarkersAndPopups(event.detail.points);
                } else {
                    generateCirclesAndPopups(event.detail.points);
                }
                closeAllInfoWindowsOnMapClick();
            });
        });
        window.addEventListener('alentoor_map:refresh', (event) => {
            resetMarkersAndPopups();
            setTimeout(() => {
                if (event.detail.useMarker) {
                    generateMarkersAndPopups(event.detail.points);
                } else {
                    generateCirclesAndPopups(event.detail.points);
                }
            }, event.detail.delay || 0);
        });
        window.addEventListener('alentoor_map:marker:highlight', (event) => {
            highlightMarkerFromEvent(event);
        });
    }
}
